import { useSelector } from 'react-redux';
// Slices
import {
  authConfigsMapSelector,
  authConfigsSelector,
} from 'slices/authConfig.slice';
import { useAppDispatch } from 'slices/store';
import { resetPasswordForUser } from 'slices/user.slice';
import { EAuthStrategy } from '@timeedit/types/lib/enums';
import type { ITEUserWithTEIdPAuthSettings } from '@timeedit/types/lib/types';
// TYPES
// COMPONENTS
import { Button, TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Popconfirm } from '@timeedit/ui-components/antd';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';

type AuthenticationProps = {
  user?: ITEUserWithTEIdPAuthSettings;
  onChange: (key: string, value: any) => void;
  onRemoveAuthSettings: () => void;
};

export const Authentication = ({
  user,
  onChange,
  onRemoveAuthSettings,
}: AuthenticationProps) => {
  const authConfigsMap = useSelector(authConfigsMapSelector);
  const authConfigs = useSelector(authConfigsSelector);
  const authConfig = user ? authConfigsMap[user?.authConfigId] : null;
  const dispatch = useAppDispatch();

  const onHandlePasswordChange = (password: string) => {
    if (!password) {
      onRemoveAuthSettings();
      return;
    }
    onChange('authSettings', { password });
  };

  return (
    <TEDrawerSection label="Authentication">
      <div
        style={{
          display: 'flex',
          gap: '8px',
          flexDirection: 'column',
          width: '33%',
          minWidth: '432px',
        }}
      >
        <TEInput.Select
          label="Authentication config"
          value={authConfig?.name}
          onChange={(authConfigId) => onChange('authConfigId', authConfigId)}
          options={authConfigs.map((config) => ({
            value: config.id,
            label: config.name,
          }))}
          tooltip="Authentication config"
          data-testid="auth-config"
        />
        <TEInput.PureText
          value={authConfig?.strategy}
          label="Strategy"
          tooltip="Method for authenticating the user"
        />
        <TEInput.Switch
          label="Locked"
          disabled={authConfig?.strategy !== EAuthStrategy.SAML2}
          checked={user?.lockedForExternalAuthentication}
          onChange={(val) => onChange('lockedForExternalAuthentication', val)}
          tooltip="Describes if the user is locked from receiving new permissions based the information provided by the external identity provider (IdP) on each successful login. This is only applicable for SAML2 authentication."
        />
        <TEInput.Password
          label="Password"
          wrapperStyle={{ flexGrow: '1', flexShrink: '0' }}
          disabled={authConfig?.strategy !== EAuthStrategy.TE_IDP}
          value={user?.authSettings?.password}
          onChange={onHandlePasswordChange}
          tooltip="Password for the user. This is only applicable for TE_IDP authentication."
          data-testid="password"
        />
        <LabelWrapper label="Reset Password" wrapperClassName="row">
          <Popconfirm
            title="This will trigger a password reset email to the selected user. Are you sure you want to proceed?"
            onConfirm={() => dispatch(resetPasswordForUser(user?.id))}
            okText="Send email"
            cancelText="Cancel"
            disabled={authConfig?.strategy !== EAuthStrategy.TE_IDP}
          >
            <Button
              size="small"
              danger
              disabled={authConfig?.strategy !== EAuthStrategy.TE_IDP}
            >
              Send email
            </Button>
          </Popconfirm>
        </LabelWrapper>
      </div>
    </TEDrawerSection>
  );
};
